// src/services/mlOpsService.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — client for the ML control plane (/api/v1/ml-ops).
// Uses the shared axios instance (services/api — baseURL already ends in /api/v1, auth header
// added by its interceptor). Every function returns the `data` field of the response and
// throws an Error whose message is the server's message (so toasts are readable).
// ─────────────────────────────────────────────────────────────────────────────
import api from './api';

const BASE = '/ml-ops';

const unwrap = (res) => {
  const body = res?.data ?? res;
  if (body && body.success === false) {
    const e = new Error(body.message || 'Request failed');
    e.code = body.code; e.verdict = body.verdict;
    throw e;
  }
  return body?.data !== undefined ? body.data : body;
};

const rethrow = (err) => {
  const body = err?.response?.data;
  const e = new Error(body?.message || err?.message || 'Request failed');
  e.status = err?.response?.status;
  e.code = body?.code;
  e.verdict = body?.verdict;
  throw e;
};

const get = (url, params, config = {}) => api.get(`${BASE}${url}`, { params, ...config }).then(unwrap, rethrow);
const post = (url, body = {}, config = {}) => api.post(`${BASE}${url}`, body, config).then(unwrap, rethrow);
const put = (url, body = {}) => api.put(`${BASE}${url}`, body).then(unwrap, rethrow);
const del = (url) => api.delete(`${BASE}${url}`).then(unwrap, rethrow);
const enc = encodeURIComponent;
// --- UPDATE (Analytics Hub) ---
const toYmd = (d) => {
  if (!d) return undefined;
  if (typeof d === 'string') return d.slice(0, 10);
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? undefined : x.toISOString().slice(0, 10);
};
const FINAL = new Set(['succeeded', 'failed', 'skipped', 'cancelled']);
const sleep = (ms, signal) => new Promise((resolve, reject) => {
  const t = setTimeout(resolve, ms);
  signal?.addEventListener?.('abort', () => { clearTimeout(t); reject(Object.assign(new Error('Aborted'), { name: 'AbortError' })); }, { once: true });
});
// --- END UPDATE ---

const mlOpsService = {
  // read
  getOverview: ({ fresh = false, signal } = {}) => get('/overview', fresh ? { fresh: 1 } : undefined, { signal }),
  getCatalog: () => get('/catalog'),
  // --- UPDATE (engines): AI engines with live numbers (SLA AI, road damage vision, road condition & RUL)
  getEngines: ({ signal } = {}) => get('/engines', undefined, { signal }),
  getEngineDetail: (key, { fresh = false, signal } = {}) => get(`/engines/${enc(key)}`, fresh ? { fresh: 1 } : undefined, { signal }),
  // --- END UPDATE
  listModels: ({ modelType, status, limit = 25, offset = 0 } = {}) =>
    get('/models', { type: modelType || undefined, status: Array.isArray(status) ? status.join(',') : status || undefined, limit, offset }),
  getModelDetail: (modelType, { signal } = {}) => get(`/models/${enc(modelType)}`, undefined, { signal }),
  compare: (modelType, a, b) => get(`/models/${enc(modelType)}/compare`, { a, b: b || undefined }),
  listJobs: ({ modelType, status, limit = 25, offset = 0 } = {}) =>
    get('/jobs', { type: modelType || undefined, status: Array.isArray(status) ? status.join(',') : status || undefined, limit, offset }),
  getJob: (id) => get(`/jobs/${enc(id)}`),
  listEvents: ({ modelType, limit = 50, before } = {}) => get('/events', { type: modelType || undefined, limit, before: before || undefined }),
  getPolicies: () => get('/policies'),
  getKpiHistory: (days = 30) => get('/insights/kpi-history', { days }),
  // --- UPDATE (round 4): latest active RUL prediction per asset, riskiest first, with a summary
  getAssetRisk: ({ limit = 8, signal } = {}) => get('/insights/asset-risk', { limit }, { signal }),
  // --- UPDATE (Analytics Hub) ---
  getBriefing: ({ startDate, endDate, focus, narrative = true, signal } = {}) =>
    get('/insights/briefing', { startDate: toYmd(startDate), endDate: toYmd(endDate), focus: focus || undefined, narrative: narrative ? undefined : 0 }, { signal, timeout: 70000 }),
  getActivity: ({ limit = 50, since, kinds } = {}) => get('/activity', { limit, since: since || undefined, kinds: Array.isArray(kinds) ? kinds.join(',') : kinds || undefined }),
  // --- END UPDATE ---

  // change
  train: (modelType, { autoPromote, note } = {}) => post(`/models/${enc(modelType)}/train`, { autoPromote, note }),
  trainAll: () => post('/train-all'),
  promote: (modelType, ref, { force = false, notes } = {}) => post(`/models/${enc(modelType)}/versions/${enc(ref)}/promote`, { force, notes }),
  rollback: (modelType) => post(`/models/${enc(modelType)}/rollback`),
  archive: (modelType, ref) => post(`/models/${enc(modelType)}/versions/${enc(ref)}/archive`),
  remove: (modelType, ref) => del(`/models/${enc(modelType)}/versions/${enc(ref)}`),
  cancelJob: (id) => post(`/jobs/${enc(id)}/cancel`),
  setPolicy: (modelType, patch) => put(`/policies/${enc(modelType)}`, patch),
  setAutopilot: (enabled) => put('/autopilot', { enabled }),
  runAutopilot: () => post('/autopilot/run'),
  resetCache: ({ modelType = null, warm = false } = {}) => post('/cache/reset', { modelType, warm }),

  // Ask FlowSense AI
  ask: ({ question, focus, history, builtIn } = {}) => post('/explain/ask', { question, focus, history, builtIn }, { timeout: 70000 }),
  explain: (key) => get(`/explain/${enc(key)}`),
  assistantStatus: () => get('/assistant/status'),
  // --- UPDATE (round 3): FlowSense AI Guide for the page at `path` (Analytics Hub: also `tab`)
  getGuide: ({ path, tab, signal } = {}) => get('/guide', { path: path || undefined, tab: tab || undefined }, { signal }),
  // --- END UPDATE
};

// --- UPDATE (legacy widgets) ---
/**
 * Polls a training run until it finishes (succeeded / failed / skipped / cancelled).
 * onProgress(job) is called on every poll. Resolves with the final job, or the last seen job
 * on timeout (job.timedOut = true). Never retries the training itself.
 */
mlOpsService.waitForJob = async (jobId, { intervalMs = 3000, timeoutMs = 20 * 60 * 1000, onProgress, signal } = {}) => {
  const until = Date.now() + timeoutMs;
  let job = null;
  for (;;) {
    job = await mlOpsService.getJob(jobId);
    try { onProgress?.(job); } catch (_) { /* caller's problem */ }
    if (!job || FINAL.has(job.status)) return job;
    if (Date.now() > until) return { ...job, timedOut: true };
    await sleep(intervalMs, signal);
  }
};

/**
 * Starts a training run and waits for its outcome. Returns a summary in the shape the older
 * widgets read: { jobId, status, decision, model_version, message, job }.
 *   status: 'production' (promoted) | 'staging' (kept / waiting for approval) | 'skipped' | 'failed' | 'running'
 * Throws only when the run could not be started or failed.
 */
mlOpsService.trainAndWait = async (modelType, { onProgress, signal, timeoutMs, autoPromote, note } = {}) => {
  const started = await mlOpsService.train(modelType, { autoPromote, note });
  const job = await mlOpsService.waitForJob(started.id, { onProgress, signal, timeoutMs });
  const DECISION = {
    promoted: 'promoted to production',
    awaiting_approval: 'better than production — waiting for approval',
    kept_champion: 'trained, production version kept (not enough improvement)',
    rejected_safeguard: 'trained, rejected by the safeguard',
  };
  if (job?.status === 'failed') {
    const e = new Error(job.error || 'Training failed.');
    e.job = job;
    throw e;
  }
  const status = job?.timedOut || job?.status === 'running' || job?.status === 'queued' ? 'running'
    : job?.status === 'skipped' ? 'skipped'
      : job?.decision === 'promoted' ? 'production' : 'staging';
  const message = status === 'running'
    ? 'Training is still running — follow it in the AI Intelligence Center.'
    : status === 'skipped'
      ? (job?.stage || 'Training skipped.')
      : `Version ${job?.model_version || ''} ${DECISION[job?.decision] || 'trained'}.`.replace(/\s+/g, ' ');
  return { jobId: started.id, status, decision: job?.decision || null, model_version: job?.model_version || null, message, job, alreadyActive: Boolean(started.alreadyActive) };
};
// --- END UPDATE ---

export default mlOpsService;
